import DownloadIcon from "@mui/icons-material/Download";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formatJakartaEndOfDay, formatJakartaStartOfDay } from "../../utils/date";
import {
  forceLogoutToLogin,
  isUnauthorizedApiResponse,
} from "../../utils/unauthorized";
import {
  fetchTicketEventOptions,
  fetchTickets,
  selectTicketEvents,
  selectTicketEventsStatus,
  selectTicketFilters,
  selectTicketItems,
  selectTicketMeta,
  selectTicketPagination,
  selectTicketSort,
  setTicketDateRange,
  setTicketEventFilter,
  setTicketSearchFilter,
} from "../../store/slices/ticketsSlice";
import { selectAccessToken } from "../../store/slices/authSlice";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";

const getFilenameFromDisposition = (contentDisposition) => {
  if (!contentDisposition) return null;
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]).replace(/["']/g, "");
  }
  const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
  if (filenameMatch?.[1]) {
    return filenameMatch[1];
  }
  return null;
};

const TicketFilters = () => {
  const dispatch = useDispatch();
  const filters = useSelector(selectTicketFilters);
  const ticketItems = useSelector(selectTicketItems);
  const ticketMeta = useSelector(selectTicketMeta);
  const pagination = useSelector(selectTicketPagination);
  const sort = useSelector(selectTicketSort);
  const events = useSelector(selectTicketEvents);
  const eventsStatus = useSelector(selectTicketEventsStatus);
  const accessToken = useSelector(selectAccessToken);
  const [searchValue, setSearchValue] = useState(filters.search);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrConfirmOpen, setQrConfirmOpen] = useState(false);
  const [qrErrorDialogOpen, setQrErrorDialogOpen] = useState(false);
  const [qrErrorMessage, setQrErrorMessage] = useState("");
  const [qrDialogError, setQrDialogError] = useState("");
  const [startRow, setStartRow] = useState("1");
  const [endRow, setEndRow] = useState("1");
  const [downloadingQr, setDownloadingQr] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteErrorDialogOpen, setDeleteErrorDialogOpen] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState("");
  const [deleteDialogError, setDeleteDialogError] = useState("");
  const [deleteStartRow, setDeleteStartRow] = useState("1");
  const [deleteEndRow, setDeleteEndRow] = useState("1");
  const [deletingTickets, setDeletingTickets] = useState(false);
  const startInputRef = useRef(null);
  const endInputRef = useRef(null);
  const menuOpen = Boolean(menuAnchor);
  const totalFilteredRows = ticketMeta?.totalItems ?? ticketItems.length;
  const qrTotalRows = Math.max(0, Number(endRow) - Number(startRow) + 1);
  const deleteTotalRows =
    Math.max(0, Number(deleteEndRow) - Number(deleteStartRow) + 1);

  useEffect(() => {
    if (eventsStatus === "idle") {
      dispatch(fetchTicketEventOptions());
    }
  }, [dispatch, eventsStatus]);

  useEffect(() => {
    setSearchValue(filters.search);
  }, [filters.search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(setTicketSearchFilter(searchValue.trim()));
    }, 350);
    return () => clearTimeout(timer);
  }, [dispatch, searchValue]);

  const handleDateChange = (key) => (event) => {
    const value = event.target.value || null;
    dispatch(
      setTicketDateRange({
        startDate: key === "startDate" ? value : filters.startDate,
        endDate: key === "endDate" ? value : filters.endDate,
      })
    );
  };

  const handleOpenDatePicker = (ref) => () => {
    const input = ref.current;
    if (!input) return;
    const wasReadOnly = input.readOnly;
    input.readOnly = false;
    if (input.showPicker) {
      try {
        input.showPicker();
      } catch {
        input.focus();
      }
    } else {
      input.focus();
    }
    input.readOnly = wasReadOnly;
  };

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.append("page", pagination.page + 1);
    params.append("pageSize", pagination.pageSize);
    if (filters.search.trim()) {
      params.append("search", filters.search.trim());
    }
    if (filters.eventId) {
      params.append("eventId", filters.eventId);
    }
    if (filters.startDate) {
      params.append("startDate", formatJakartaStartOfDay(filters.startDate));
    }
    if (filters.endDate) {
      params.append("endDate", formatJakartaEndOfDay(filters.endDate));
    }
    if (sort?.field) {
      params.append("sortBy", sort.field);
      params.append("sortDirection", sort.direction || "asc");
    }
    return params.toString();
  }, [filters, pagination, sort]);

  const handleExport = async () => {
    setExportError(null);
    setExporting(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/tickets/export?${queryString}`,
        {
          headers: {
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        }
      );

      if (isUnauthorizedApiResponse(response)) {
        forceLogoutToLogin();
        return;
      }

      if (!response.ok) {
        throw new Error(response.statusText || "Failed to download file.");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `tickets-${new Date().toISOString()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setExportError(error.message || "Failed to download file.");
    } finally {
      setExporting(false);
    }
  };
  const handleMenuOpen = (event) => setMenuAnchor(event.currentTarget);
  const handleMenuClose = () => setMenuAnchor(null);

  const handleDownloadQr = () => {
    handleMenuClose();
    setExportError(null);
    setQrDialogError("");
    setStartRow("1");
    setEndRow(String(Math.max(totalFilteredRows, 1)));
    setQrDialogOpen(true);
  };

  const handleDeleteTicket = () => {
    handleMenuClose();
    setExportError(null);
    setDeleteDialogError("");
    setDeleteStartRow("1");
    setDeleteEndRow(String(Math.max(totalFilteredRows, 1)));
    setDeleteDialogOpen(true);
  };

  const handleProceedQr = () => {
    const parsedStart = Number(startRow);
    const parsedEnd = Number(endRow);
    if (
      !Number.isInteger(parsedStart) ||
      !Number.isInteger(parsedEnd) ||
      parsedStart <= 0 ||
      parsedEnd <= 0
    ) {
      setQrDialogError("Start Row and End Row must be positive integers.");
      return;
    }
    if (parsedStart > parsedEnd) {
      setQrDialogError("Start Row cannot be greater than End Row.");
      return;
    }
    if (parsedEnd > Math.max(totalFilteredRows, 1)) {
      setQrDialogError(`End Row cannot exceed ${Math.max(totalFilteredRows, 1)}.`);
      return;
    }

    setQrDialogError("");
    setQrDialogOpen(false);
    setQrConfirmOpen(true);
  };

  const handleConfirmQrDownload = async () => {
    const parsedStart = Number(startRow);
    const parsedEnd = Number(endRow);
    const startIndex = parsedStart - 1;
    const endIndex = parsedEnd - 1;
    setDownloadingQr(true);
    setExportError(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/tickets/qr-export?${queryString}&startIndex=${startIndex}&endIndex=${endIndex}`,
        {
          headers: {
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        }
      );

      const contentType = response.headers.get("content-type")?.toLowerCase() || "";
      if (contentType.includes("application/json")) {
        const payload = await response.json();
        if (isUnauthorizedApiResponse(response, payload)) {
          forceLogoutToLogin();
          return;
        }
        if (!response.ok || ![200, 201, 204].includes(payload?.resultCode)) {
          throw new Error(payload?.resultDesc || "Failed to download QR.");
        }
      } else if (isUnauthorizedApiResponse(response)) {
        forceLogoutToLogin();
        return;
      } else if (!response.ok) {
        throw new Error(response.statusText || "Failed to download QR.");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const disposition = response.headers.get("content-disposition");
      const filenameFromHeader = getFilenameFromDisposition(disposition);
      link.download =
        filenameFromHeader || `tickets-qr-${new Date().toISOString()}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setQrConfirmOpen(false);
    } catch (error) {
      setQrConfirmOpen(false);
      setQrErrorMessage(error.message || "Failed to download QR.");
      setQrErrorDialogOpen(true);
    } finally {
      setDownloadingQr(false);
    }
  };

  const handleProceedDelete = () => {
    const parsedStart = Number(deleteStartRow);
    const parsedEnd = Number(deleteEndRow);
    if (
      !Number.isInteger(parsedStart) ||
      !Number.isInteger(parsedEnd) ||
      parsedStart <= 0 ||
      parsedEnd <= 0
    ) {
      setDeleteDialogError("Start Row and End Row must be positive integers.");
      return;
    }
    if (parsedStart > parsedEnd) {
      setDeleteDialogError("Start Row cannot be greater than End Row.");
      return;
    }
    if (parsedEnd > Math.max(totalFilteredRows, 1)) {
      setDeleteDialogError(
        `End Row cannot exceed ${Math.max(totalFilteredRows, 1)}.`
      );
      return;
    }

    setDeleteDialogError("");
    setDeleteDialogOpen(false);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    const parsedStart = Number(deleteStartRow);
    const parsedEnd = Number(deleteEndRow);
    const startIndex = parsedStart - 1;
    const endIndex = parsedEnd - 1;
    setDeletingTickets(true);
    setExportError(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/tickets/bulk-delete?${queryString}&startIndex=${startIndex}&endIndex=${endIndex}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        }
      );

      const payload = await response.json().catch(() => null);
      if (isUnauthorizedApiResponse(response, payload)) {
        forceLogoutToLogin();
        return;
      }
      if (!response.ok || ![200, 201, 204].includes(payload?.resultCode)) {
        throw new Error(payload?.resultDesc || "Failed to delete tickets.");
      }

      setDeleteConfirmOpen(false);
      dispatch(fetchTickets());
    } catch (error) {
      setDeleteConfirmOpen(false);
      setDeleteErrorMessage(error.message || "Failed to delete tickets.");
      setDeleteErrorDialogOpen(true);
    } finally {
      setDeletingTickets(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={2}
        alignItems={{ lg: "center" }}
        justifyContent="space-between"
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          sx={{ width: { xs: "100%", lg: "auto" }, flexGrow: 1 }}
        >
          <TextField
            fullWidth
            label="Search by Ticket ID / Barcode"
            placeholder="Enter ticket ID or barcode"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            fullWidth
            label="Event"
            value={filters.eventId}
            onChange={(event) => dispatch(setTicketEventFilter(event.target.value))}
            sx={{ minWidth: { md: 220 } }}
          >
            <MenuItem value="">All events</MenuItem>
            {events.map((event) => (
              <MenuItem key={event.id} value={event.id}>
                {event.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
          }}
        >
          <IconButton
            onClick={handleMenuOpen}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              width: 48,
              height: 48,
            }}
          >
            {exporting ? <CircularProgress size={20} /> : <MoreVertIcon />}
          </IconButton>
        </Box>
      </Stack>
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          elevation: 6,
          sx: {
            boxShadow: "0px 22px 45px rgba(15, 23, 42, 0.18)",
            border: "1px solid rgba(148, 163, 184, 0.16)",
            background:
              "radial-gradient(circle at top right, rgba(14, 165, 233, 0.24), transparent 55%), #ffffff",
            borderRadius: 3,
            mt: 1,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleMenuClose();
            handleExport();
          }}
          disabled={exporting}
        >
          <DownloadIcon fontSize="small" style={{ marginRight: 8 }} />
          {exporting ? "Preparing..." : "Download Excel"}
        </MenuItem>
        <MenuItem onClick={handleDownloadQr}>
          <QrCode2Icon fontSize="small" style={{ marginRight: 8 }} />
          Download QR
        </MenuItem>
        <MenuItem onClick={handleDeleteTicket}>
          <DeleteOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
          Delete Ticket
        </MenuItem>
      </Menu>
      <Dialog
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Download Ticket QR</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {qrDialogError && <Alert severity="error">{qrDialogError}</Alert>}
            <TextField
              label="Start Row"
              type="number"
              value={startRow}
              onChange={(event) => {
                setStartRow(event.target.value);
                if (qrDialogError) setQrDialogError("");
              }}
              fullWidth
            />
            <TextField
              label="End Row"
              type="number"
              value={endRow}
              onChange={(event) => {
                setEndRow(event.target.value);
                if (qrDialogError) setQrDialogError("");
              }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleProceedQr}>
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={qrConfirmOpen}
        onClose={() => setQrConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Confirm QR Download</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to download Ticket QR for rows {startRow} to {endRow} (total {qrTotalRows} rows)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setQrConfirmOpen(false);
              setQrDialogOpen(true);
            }}
            disabled={downloadingQr}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmQrDownload}
            disabled={downloadingQr}
            startIcon={
              downloadingQr ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {downloadingQr ? "Downloading..." : "Download"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={qrErrorDialogOpen}
        onClose={() => setQrErrorDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Unable to Download Ticket QR</DialogTitle>
        <DialogContent>
          <Typography>{qrErrorMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setQrErrorDialogOpen(false);
              setQrDialogOpen(true);
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete Ticket</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {deleteDialogError && <Alert severity="error">{deleteDialogError}</Alert>}
            <TextField
              label="Start Row"
              type="number"
              value={deleteStartRow}
              onChange={(event) => {
                setDeleteStartRow(event.target.value);
                if (deleteDialogError) setDeleteDialogError("");
              }}
              fullWidth
            />
            <TextField
              label="End Row"
              type="number"
              value={deleteEndRow}
              onChange={(event) => {
                setDeleteEndRow(event.target.value);
                if (deleteDialogError) setDeleteDialogError("");
              }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleProceedDelete}>
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Confirm Ticket Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete tickets for rows {deleteStartRow} to{" "}
            {deleteEndRow} (total {deleteTotalRows} rows)?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              setDeleteDialogOpen(true);
            }}
            disabled={deletingTickets}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deletingTickets}
          >
            {deletingTickets ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteErrorDialogOpen}
        onClose={() => setDeleteErrorDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Unable to Delete Tickets</DialogTitle>
        <DialogContent>
          <Typography>{deleteErrorMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteErrorDialogOpen(false);
              setDeleteDialogOpen(true);
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ width: "100%" }}
      >
        <TextField
          fullWidth
          type="date"
          label="Start Date"
          InputLabelProps={{ shrink: true }}
          value={filters.startDate || ""}
          onChange={handleDateChange("startDate")}
          inputRef={startInputRef}
          onClick={handleOpenDatePicker(startInputRef)}
          InputProps={{
            readOnly: true,
          }}
          inputProps={{ style: { cursor: "pointer" } }}
          sx={{ minWidth: { sm: 220 } }}
        />
        <TextField
          fullWidth
          type="date"
          label="End Date"
          InputLabelProps={{ shrink: true }}
          value={filters.endDate || ""}
          onChange={handleDateChange("endDate")}
          inputRef={endInputRef}
          onClick={handleOpenDatePicker(endInputRef)}
          InputProps={{
            readOnly: true,
          }}
          inputProps={{ style: { cursor: "pointer" } }}
          sx={{ minWidth: { sm: 220 } }}
        />
      </Stack>
      {exporting && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Preparing Excel file, this may take a moment...
          </Typography>
        </Stack>
      )}
      {exportError && (
        <Typography variant="body2" color="error">
          {exportError}
        </Typography>
      )}
    </Stack>
  );
};

export default TicketFilters;
